import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Quote } from '../quote';
import { formatPrice } from '../../quotes/priceFormatter';
import QuoteWizardContext from '../QuoteWizardContext';
import { Input } from '@ami/react/core';
import classNames from 'classnames';
import {
  getTotalMsrpColumnClassNames,
  getTotalDNetColumnClassNames,
} from '../../quotes/quotePriceMode';
import { OverallDiscountsComponent } from '../../OverallDiscountsComponent';
import { appConfig } from '@ami/react/core';
import { isNumber } from 'lodash';
import { PriceBanner } from '../PricingAndLeadTimesBanner';

const priceValueClassName = 'quote-price-value';

const Header = ({ msrpClassNames, dNetClassNames }) => (
  <div className="row justify-content-end">
    <div className="col-6 quote-price-label" />
    <h5 className={classNames(priceValueClassName, msrpClassNames)}>MSRP</h5>
    <h5 className={classNames(priceValueClassName, dNetClassNames)}>D. NET</h5>
  </div>
);

const PriceGeneric = ({
  label,
  msrp,
  price,
  msrpClassNames,
  dNetClassNames,
}) => (
  <div
    className={
      'row justify-content-end' +
      (label.toLowerCase() === 'total' ? ' totalPrice ' : '')
    }
  >
    <h5 className="col-6 quote-price-label">{label}</h5>
    <h5 className={classNames(priceValueClassName, msrpClassNames, 'mb-0')}>
      {msrp}
    </h5>
    <h5 className={classNames(priceValueClassName, dNetClassNames, 'mb-0')}>
      {price}
    </h5>
  </div>
);

const Price = ({ label, msrp, price, msrpClassNames, dNetClassNames }) => (
  <PriceGeneric
    label={label}
    msrp={isNumber(msrp) ? formatPrice(msrp) : msrp}
    price={isNumber(price) ? formatPrice(price) : price}
    msrpClassNames={msrpClassNames}
    dNetClassNames={dNetClassNames}
  />
);

export default class ReviewQuotePrice extends React.Component {
  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    selectedPriceMode: PropTypes.string.isRequired,
    isoCurrencySymbol: PropTypes.string.isRequired,
    onOverallDiscountsUpdate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  render() {
    const {
      quote,
      selectedPriceMode,
      isoCurrencySymbol,
      onOverallDiscountsUpdate,
      onUpdate,
    } = this.props;

    const maskPricingAndLeadTimes =   quote.maskPricingAndLeadTimes;
    const productsSubtotal = quote.getProductsSubtotal();
    const productsMsrp = quote.getProductsSubtotalMsrp();
    const msrpClassNames = getTotalMsrpColumnClassNames(selectedPriceMode);
    const dNetClassNames = getTotalDNetColumnClassNames(selectedPriceMode);
    const freightSurcharge = quote.getFreightSurcharge();
    const freightSurchargeMsrp = quote.getFreightSurchargeMsrp();

    return (
      <QuoteWizardContext.Consumer>
        {({ showCustomLineItems }) => (
          <Fragment>
            <Header
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
            />
              { !maskPricingAndLeadTimes && 
                 <div>
            <Price
              label="Products"
              msrp={productsMsrp + quote.getCustomLineItemsSubTotalMsrp()}
              price={productsSubtotal + quote.getCustomLineItemsSubTotal()}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
            />
            {(!quote.freightSurchargeOverridden || appConfig.isDealerApp) && (
              <Price
                label="Freight"
                msrp={
                  freightSurchargeMsrp === 0 ? 'INCLUDED' : freightSurchargeMsrp
                }
                price={freightSurcharge === 0 ? 'INCLUDED' : freightSurcharge}
                msrpClassNames={msrpClassNames}
                dNetClassNames={dNetClassNames}
              />
            )}
            {quote.freightSurchargeOverridden && !appConfig.isDealerApp && (
              <PriceGeneric
                label="Freight"
                msrp={formatPrice(freightSurchargeMsrp)}
                price={
                  <Input
                    prependValue="$"
                    id="freightSurcharge"
                    className="inline-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={freightSurcharge}
                    onChange={(e) => {
                      const copy = quote.clone();
                      var parsedInput = parseFloat(e.target.value);
                      copy.freightSurcharge = isNaN(parsedInput)
                        ? undefined
                        : parsedInput;
                      onUpdate(copy);
                    }}
                    style={{ textAlign: 'right' }}
                  />
                }
                msrpClassNames={msrpClassNames}
                dNetClassNames={dNetClassNames}
              />
            )}
            {appConfig.isManagementApp && (
              <div className="row justify-content-start">
                <div className="col-6">
                  {!quote.freightSurchargeOverridden && (
                    <button
                      type="button"
                      className="btn btn-link ps-0 pt-0 pe-2 pb-2 m-0"
                      onClick={(e) => {
                        e.preventDefault();
                        const copy = quote.clone();
                        copy.freightSurchargeOverridden = true;
                        onUpdate(copy);
                      }}
                    >
                      Add freight override
                    </button>
                  )}
                  {quote.freightSurchargeOverridden && (
                    <button
                      type="button"
                      className="btn btn-link ps-0 pt-0 pe-2 pb-2 m-0"
                      onClick={(e) => {
                        e.preventDefault();
                        const copy = quote.clone();
                        copy.freightSurchargeOverridden = false;
                        onUpdate(copy);
                      }}
                    >
                      Remove freight override
                    </button>
                  )}
                </div>
              </div>
            )}
            {quote.supportsInstallation && (
              <Price
                label="Installation"
                msrp={quote.getInstallationSubTotalMsrp()}
                price={quote.getInstallationSubTotal()}
                msrpClassNames={msrpClassNames}
                dNetClassNames={dNetClassNames}
              />
            )}
            <Price
              label="SubTotal"
              msrp={quote.getSubTotalMsrp()}
              price={quote.getSubTotal()}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
            />
            <OverallDiscountsComponent
              overallDiscounts={quote.overallDiscounts}
              forceVisible={false}
              subTotal={
                quote.getSubTotal()
              }
              onUpdate={onOverallDiscountsUpdate}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
              priceValueClassName={priceValueClassName}
              discountSubTotal={quote.getDiscountedSubTotal()}
              discountSubTotalMsrp={quote.getDiscountSubTotalMsrp()}
            />
            <Price
              label="Tax"
              msrp={quote.getTaxMsrp()}
              price={quote.getTax()}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
            />
            </div>
            }
            <Price
              label={`Total (${isoCurrencySymbol})`}
              msrp={ maskPricingAndLeadTimes ? "N/A" : quote.getTotalMsrp()}
              price={ maskPricingAndLeadTimes ? "N/A" : quote.getTotal()}
              msrpClassNames={msrpClassNames}
              dNetClassNames={dNetClassNames}
            />
               { maskPricingAndLeadTimes && <PriceBanner />}
          </Fragment>
        )}
      </QuoteWizardContext.Consumer>
    );
  }
}
